<template>
  <div
    class="operation-report-show-body-form-item flex flex-col"
    :class="{
      required: required,
      disabled,
      'is-editing': isEditing,
      'has-errors': hasErrors,
    }"
  >
    <div
      class="form-item-label aisea-text-16-700-dark-gray"
      :class="{
        required: required,
        disabled,
        'is-editing': isEditing,
        'has-errors': hasErrors,
      }"
    >
      {{ label }}
    </div>
    <!-- ファイル・画像がある時（拡張子で画像はプレビュー、それ以外はファイル名表示） -->
    <div
      class="form-item-value-files flex flex-wrap flex-col gap-3"
      v-if="hasFile"
    >
      <div
        :id="valueFile.id"
        class="vs-con-loading__container relative"
        :class="
          isFilePreview(valueFile) ? 'file-preview-container' : 'file-container'
        "
        v-for="valueFile in valueFiles"
        :key="valueFile.hash"
      >
        <div
          :class="
            isFilePreview(valueFile)
              ? 'file-preview-container-loading'
              : 'file-container-loading'
          "
          class="w-full h-full"
          v-if="valueFile.loading"
        ></div>
        <!-- 画像: プレビュー + ドロップダウン -->
        <div
          class="file-preview-container-success w-full h-full"
          v-else-if="valueFile.success && isFilePreview(valueFile)"
        >
          <img :src="valueFile.url" :alt="valueFile.name" class="file-preview" />
          <div class="file-preview-container-header">
            <vs-dropdown
              :color="AISEA_COLOR.BLUE"
              class="dropdown-list cursor-pointer"
              vs-trigger-click
            >
              <feather-icon
                class="action-button-icon"
                svgClasses="h-6 w-6"
                icon="MoreVerticalIcon"
              ></feather-icon>
              <vs-dropdown-menu class="content-dropdown-menu-operation">
                <vs-dropdown-item
                  v-for="{ event, onClick, label, icon } in eventList"
                  :key="event"
                  @click="() => onClick(valueFile)"
                >
                  <span class="flex items-center">
                    <feather-icon
                      :icon="icon"
                      svgClasses="h-4 w-4"
                      class="mr-2"
                    />
                    <span>{{ label }}</span>
                  </span>
                </vs-dropdown-item>
              </vs-dropdown-menu>
            </vs-dropdown>
          </div>
        </div>
        <!-- 画像以外: ファイル名 + ドロップダウン -->
        <div
          class="file-container-success w-full h-full"
          v-else-if="valueFile.success"
        >
          <div class="file-name-display aisea-text-16-400-dark-gray">
            {{ getDisplayFileName(valueFile.name) }}
          </div>
          <div class="file-container-header">
            <vs-dropdown
              :color="AISEA_COLOR.BLUE"
              class="dropdown-list cursor-pointer"
              vs-trigger-click
            >
              <feather-icon
                class="action-button-icon"
                svgClasses="h-6 w-6"
                icon="MoreVerticalIcon"
              ></feather-icon>
              <vs-dropdown-menu class="content-dropdown-menu-operation">
                <vs-dropdown-item
                  v-for="{ event, onClick, label, icon } in fileEventList"
                  :key="event"
                  @click="() => onClick(valueFile)"
                >
                  <span class="flex items-center">
                    <feather-icon
                      :icon="icon"
                      svgClasses="h-4 w-4"
                      class="mr-2"
                    />
                    <span>{{ label }}</span>
                  </span>
                </vs-dropdown-item>
              </vs-dropdown-menu>
            </vs-dropdown>
          </div>
        </div>
        <error-message :fileReadErrorMessage="fileReadErrorMessage" v-else />
      </div>
      <!-- 画像のみライトボックスでプレビュー -->
      <light-box
        v-if="hasFile && filePreviewFiles.length > 0"
        ref="lightbox"
        :media="previewFiles"
        :show-light-box="false"
        :show-thumbs="false"
        :show-caption="false"
        :disableScroll="false"
      >
      </light-box>
    </div>
    <!-- ファイルがない時 -->
    <template v-else>
      <!-- 詳細 -->
      <div
        class="form-item-value aisea-text-16-400-dark-gray"
        v-if="!isEditing"
      >
        {{ fallbackValue }}
      </div>
      <!-- 編集 -->
      <template v-else>
        <div class="file-select-button-wrapper">
          <!-- ファイル・画像選択ボタン -->
          <file-select-button @click="showUploadDialog" :disabled="disabled" />
          <!-- ファイル選択ダイアログ -->
          <file-uploader-dialog
            :active="isActiveUploadDialog"
            @close="closeUploadDialog"
            @cancel="closeUploadDialog"
            @accept="onUpload"
          />
        </div>
      </template>
    </template>
    <!-- nullable label -->
    <template v-if="isEditing">
      <input
        type="hidden"
        :name="name"
        :value="valueForValidate"
        v-validate="vValidate"
      />
      <div
        class="nullable-label flex items-center aisea-text-16-400-dark-gray"
        v-if="hasNullableLabel"
      >
        <vs-checkbox
          v-model="nullableChecked"
          :color="nullableCheckBoxColor"
          :disabled="disabled"
          @change="onChange"
        >
          <span>{{ nullableLabel }}</span>
        </vs-checkbox>
      </div>
    </template>
  </div>
</template>

<script>
// 12: SINGLE_IMAGE_FILE … 画像・ファイル両対応。拡張子でプレビュー（画像）／ファイル名表示を切り替え
import LightBox from "vue-image-lightbox";
import ErrorMessage from "./ErrorMessage";
import FileSelectButton from "./FileSelectButton";
import FileUploaderDialog from "./FileUploaderDialog.vue";
import { getDisplayFileName, isImageFileByName } from "@/utils/operations";
import { ColorMixin } from "@/mixins";
const { AISEA_COLOR } = ColorMixin.data();
import isString from "lodash/isString";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

const EVENTS = {
  PREVIEW: "preview",
  DOWNLOAD: "download",
  DELETE: "delete",
  CHANGE: "change",
  UPLOAD: "upload",
  SHOW_DIALOG: "show-dialog",
  CLOSE_DIALOG: "close-dialog",
};

export default {
  name: "OperationReportShowBodyFormItemFile",
  components: {
    LightBox,
    ErrorMessage,
    FileSelectButton,
    FileUploaderDialog,
  },
  props: {
    label: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array, // {hash: string; url: string; success: boolean; loading: boolean}[]
      default: () => [],
    },
    fallbackValue: {
      type: String,
      default: "ー",
    },
    // 編集中かどうか
    isEditing: {
      type: Boolean,
      default: false,
    },
    nullableLabel: {
      type: String,
      required: false,
    },
    isNullableChecked: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      required: true,
    },
    isActiveUploadDialog: {
      type: Boolean,
      default: false,
    },
    hasReportFormVersionItemValue: {
      type: Boolean,
      default: false,
    },
    defaultValue: {
      type: Array,
      required: false,
    },
  },

  data() {
    return {
      nullableChecked: false,
      isDefaultValueInserted: false, // default値を挿入したかどうか -> defaultValueの更新が無限ループになるのを防ぐのに使う
    };
  },

  watch: {
    value: {
      handler() {
        const {
          hasReportFormVersionItemValue,
          defaultValue,
          onChangeByDefaultValue,
        } = this;
        if (
          !hasReportFormVersionItemValue &&
          !isNil(defaultValue) &&
          Array.isArray(defaultValue) &&
          defaultValue.length > 0
        ) {
          if (this.isDefaultValueInserted) return;
          onChangeByDefaultValue(); // デフォルト値を使用する場合に、そのまま更新リクエストできるようにする
        }
        this.isDefaultValueInserted = true;
      },
      immediate: true,
    },
    valueFiles: {
      handler() {
        this.$nextTick(() => {
          this.valueFiles.forEach(valueFile => {
            if (valueFile.loading) {
              this.$vs.loading({
                type: "border",
                container: `#${valueFile.id}`,
                scale: 0.6,
              }); // ローディング中ならローディング表示
            } else {
              this.$vs.loading.close(`#${valueFile.id} > .con-vs-loading`);
            }
          });
        });
      },
    },

    isNullableChecked: {
      handler() {
        this.nullableChecked = this.isNullableChecked;
      },
      immediate: true,
    },
    vValidate(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.$nextTick(async () => {
        await this.$validator.validateAll(); // nullableLabel の変更時にバリデーションを効かせる
      });
    },
    valueForValidate(newVal, oldVal) {
      if (newVal === oldVal) return;
      this.$nextTick(async () => {
        await this.$validator.validateAll();
      });
    },
  },
  computed: {
    valueForValidate() {
      const { value } = this;
      if (!Array.isArray(value)) return undefined;
      return value[0] && value[0].name; // validationにファイル名を用いる
    },
    hasFile() {
      const { value } = this;
      return Array.isArray(value) && value.length > 0;
    },
    valueFiles() {
      if (!this.hasFile) return [];
      return this.value.map(valueFile => {
        const id = `id-${valueFile.hash}`;
        return {
          ...valueFile,
          id,
        };
      });
    },
    AISEA_COLOR() {
      return AISEA_COLOR;
    },
    /** 画像用: プレビュー・ダウンロード・削除 */
    eventList() {
      const { onPreview, onDownload, onDelete, isEditing } = this;
      return [
        {
          label: this.$t("Views.Operation.ImageManipulation.Preview.label"),
          icon: "EyeIcon",
          event: EVENTS.PREVIEW,
          onClick: onPreview,
        },
        {
          label: this.$t("Views.Operation.ImageManipulation.Download.label"),
          icon: "DownloadIcon",
          event: EVENTS.DOWNLOAD,
          onClick: onDownload,
        },
        {
          label: this.$t("Views.Operation.ImageManipulation.Delete.label"),
          icon: "TrashIcon",
          event: EVENTS.DELETE,
          onClick: onDelete,
        },
      ].filter(({ event }) => {
        if (event !== EVENTS.DELETE) return true;
        return isEditing; // 削除ボタンは編集中の時だけ表示
      });
    },
    /** 画像以外ファイル用: ダウンロード・削除（プレビューなし） */
    fileEventList() {
      const { onDownload, onDelete, isEditing } = this;
      return [
        {
          label: this.$t("Views.Operation.ImageManipulation.Download.label"),
          icon: "DownloadIcon",
          event: EVENTS.DOWNLOAD,
          onClick: onDownload,
        },
        {
          label: this.$t("Views.Operation.ImageManipulation.Delete.label"),
          icon: "TrashIcon",
          event: EVENTS.DELETE,
          onClick: onDelete,
        },
      ].filter(({ event }) => event !== EVENTS.DELETE || isEditing);
    },
    /** 拡張子が画像のもののみ（ライトボックス用） */
    filePreviewFiles() {
      return this.valueFiles.filter(v => isImageFileByName(v.name));
    },
    previewFiles() {
      return this.filePreviewFiles.map(({ url }) => ({ src: url }));
    },
    fileReadErrorMessage() {
      return this.$t("Views.Operation.ImageManipulation.Read.Fail.text"); // ファイル読み込みでエラー出たら表示
    },
    isNullable() {
      if (!this.hasNullableLabel) return !this.required;
      return this.nullableChecked === true;
    },
    vValidate() {
      const required = this.required && !this.isNullable ? "required|" : "";
      return `${required}`;
    },
    hasErrors() {
      return this.errors.has(this.name);
    },
    hasNullableLabel() {
      const { nullableLabel } = this;
      return isString(nullableLabel) && !isEmpty(nullableLabel);
    },
    nullableCheckBoxColor() {
      return AISEA_COLOR.BLUE;
    },
  },

  methods: {
    /** 拡張子で画像ならプレビュー表示、それ以外はファイル名表示 */
    isFilePreview(valueFile) {
      return isImageFileByName(valueFile.name);
    },
    getDisplayFileName(name) {
      return getDisplayFileName(name);
    },
    onPreview(file) {
      const idx = this.filePreviewFiles.findIndex(f => f.hash === file.hash);
      if (idx >= 0 && this.$refs.lightbox) this.$refs.lightbox.showImage(idx);
      this.$emit(EVENTS.PREVIEW, file);
    },
    onDownload(file) {
      this.$emit(EVENTS.DOWNLOAD, file);
    },
    onDelete(file) {
      this.$emit(EVENTS.DELETE, file);
    },
    showUploadDialog() {
      this.$emit(EVENTS.SHOW_DIALOG);
    },
    closeUploadDialog() {
      this.$emit(EVENTS.CLOSE_DIALOG);
    },
    onUpload(documents) {
      this.$emit(EVENTS.UPLOAD, documents);
    },
    onChange() {
      const { disabled, value, nullableChecked } = this;
      if (disabled) return;
      this.$emit(EVENTS.CHANGE, { value, nullableChecked });
    },
    // デフォルト値でstoreを更新する
    onChangeByDefaultValue() {
      const { disabled, defaultValue, nullableChecked } = this;
      if (disabled) return;
      this.$emit(EVENTS.CHANGE, { value: defaultValue, nullableChecked });
    },
  },

  beforeDestroy() {
    this.isDefaultValueInserted = false;
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";
.operation-report-show-body-form-item {
  min-height: 90px;
  padding: 18px 0;
  row-gap: 4px;

  .file-preview-container {
    width: 200px;
    height: 150px;
    border-radius: 6px;
    overflow: hidden;
    background-color: $aisea-light-gray;
  }
  .file-container {
    width: 100%;
    min-height: 48px;
    border-radius: 6px;
    overflow: hidden;
    background-color: $aisea-light-gray;
    padding: 12px 40px 12px 12px;
  }
  .file-name-display {
    word-break: break-all;
    padding-right: 32px;
  }
  .file-preview {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .file-preview-container-header,
  .file-container-header,
  .dropdown-list {
    position: absolute;
    top: 10px;
    right: 10px;
  }

  .form-item-label.is-editing {
    &.has-errors {
      color: $aisea-red; // エラー発生時に項目名を赤くする
    }

    // 必須の赤い点
    &.required:after {
      content: " *";
      font-size: 14px;
      color: $aisea-red;
      font-weight: 700;
      font-family: $aisea-font-family;
    }
  }

  &.is-editing.disabled {
    .form-item-value-files,
    .file-select-button-wrapper {
      pointer-events: none;
    }
    .form-item-value-files > * {
      opacity: 0.6;
    }
  }

  .nullable-label {
    gap: 20px;
  }
}

.content-dropdown-menu-operation {
  width: 200px;

  ::v-deep .vs-dropdown--menu--after {
    display: none; // 吹き出しを消す
  }
}
</style>
